import { Col, ColType, Crud, CrudApi } from "../../comp/crud";
import { TextCell } from "../../comp/cell";
import "../clientConfig/index.scss";
import { formatDate } from "../../utils";
import { Tag, Button, Toast, Tooltip } from "@douyinfe/semi-ui";
import { IconPlay, IconLoading } from "@douyinfe/semi-icons";
import React, { useEffect, useRef, useState } from "react";
import AudioPlayerModal from "../../comp/AudioPlayerModal";
import { AiCoachApi } from "../../api/aiCoach";

// 通话状态映射
const callStatusMap: Record<string, { label: string; color: string }> = {
  // ENABLED: { label: "启用", color: "green" },
  // DISABLED: { label: "停用", color: "grey" },
  CALLING: { label: "拨号中", color: "blue" },
  CONNECTED: { label: "已接通", color: "green" },
  ENDED: { label: "已接通", color: "green" },
  MISSED: { label: "未接通", color: "red" },
};

/** 将秒数格式化为 mm:ss */
function formatSeconds(s: number | null | undefined): string {
  if (s === null || s === undefined || s < 0) return "-";
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

/** 转写结果轮询：3 秒一次，最多 100 次（约 5 分钟），超时自动停止 */
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 100;

export default function CallRecord() {
  const [audioVisible, setAudioVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioTitle, setAudioTitle] = useState("");
  const [audioFileName, setAudioFileName] = useState("");
  const [audioTranscript, setAudioTranscript] = useState("");
  const crudApiRef = useRef<CrudApi | null>(null);
  const [transcribing, setTranscribing] = useState<Set<number>>(new Set());
  const [transcriptTexts, setTranscriptTexts] = useState<Map<number, string>>(new Map());
  const pollTimers = useRef<Map<number, number>>(new Map());
  // 转写约束（开关 + 时长窗口），用于按钮预校验；拉取失败时退化为仅后端校验
  const [transcriptLimits, setTranscriptLimits] = useState<{ enabled: boolean; minDurationSec: number; maxDurationSec: number } | null>(null);

  // 拉取转写约束（仅一次）
  useEffect(() => {
    AiCoachApi.getTranscriptLimits()
      .then((resp) => setTranscriptLimits(resp.data))
      .catch(() => {});
  }, []);

  const closeAudio = () => {
    setAudioVisible(false);
    setAudioUrl("");
    setAudioTranscript("");
    setAudioFileName("");
  };

  // 组件卸载时清理轮询定时器
  useEffect(() => {
    return () => {
      pollTimers.current.forEach((timer) => clearTimeout(timer));
      pollTimers.current.clear();
    };
  }, []);

  /** 停止某条记录的转写轮询 */
  const stopPolling = (callLogId: number) => {
    const timer = pollTimers.current.get(callLogId);
    if (timer != null) {
      clearTimeout(timer);
      pollTimers.current.delete(callLogId);
    }
    setTranscribing((prev) => { const s = new Set(prev); s.delete(callLogId); return s; });
  };

  /** 开始轮询某条记录的转写结果（自调度 setTimeout，慢响应时不会重叠请求） */
  const startPolling = (callLogId: number) => {
    stopPolling(callLogId);
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const resp = await AiCoachApi.getCallLogTranscript(callLogId);
        const status = (resp as any).data?.status;
        const text = (resp as any).data?.text;
        if (status === "ready" && text) {
          stopPolling(callLogId);
          setTranscriptTexts((prev) => new Map(prev).set(callLogId, text));
          Toast.success("转写完成");
          crudApiRef.current?.refresh();
          return;
        }
        if (status === "failed") {
          stopPolling(callLogId);
          const err = (resp as any).data?.error;
          Toast.error(`转写失败${err ? "：" + err : ""}`);
          return;
        }
      } catch (e: any) {
        // ApiClient 已统一 Toast 错误；这里只区分：网络抖动继续轮询，业务错误（如权限）立即停止
        if (!(e instanceof TypeError) && e?.message !== "Failed to fetch") {
          stopPolling(callLogId);
          return;
        }
      }
      if (attempts >= POLL_MAX_ATTEMPTS) {
        stopPolling(callLogId);
        Toast.warning("转写耗时较长，请稍后刷新查看结果");
        return;
      }
      pollTimers.current.set(callLogId, window.setTimeout(tick, POLL_INTERVAL_MS));
    };
    pollTimers.current.set(callLogId, window.setTimeout(tick, POLL_INTERVAL_MS));
  };

  /** 点击"转文字"按钮 */
  const handleTranscribe = async (callLogId: number) => {
    try {
      setTranscribing((prev) => new Set(prev).add(callLogId));
      const resp = await AiCoachApi.submitTranscript(callLogId);
      const status = (resp as any).data;
      if (status === "ready") {
        // 已有结果，直接获取
        const tResp = await AiCoachApi.getCallLogTranscript(callLogId);
        const text = (tResp as any).data?.text;
        if (text) {
          setTranscriptTexts((prev) => new Map(prev).set(callLogId, text));
          crudApiRef.current?.refresh();
        }
        stopPolling(callLogId);
        Toast.info("已有转写结果");
      } else {
        // submitted / processing，开始轮询
        Toast.info("已提交转写，请稍候...");
        startPolling(callLogId);
      }
    } catch (e: any) {
      stopPolling(callLogId);
      // ApiClient.request 的 handleError 已对业务错误统一 Toast（含时长窗口提示），这里只兜底网络异常
      if (e instanceof TypeError || e?.message === "Failed to fetch") {
        Toast.error("网络异常，提交转写失败");
      }
    }
  };

  /**
   * 打开"播放 + 转写"联动弹窗：
   * 立即用缓存或列表预览打开，转写全文异步补齐（列表接口只回 200 字预览）
   */
  const openPlayerModal = async (row: any) => {
    const cached = transcriptTexts.get(row.id);
    setAudioUrl(row.recordFile);
    setAudioTitle(row.recordName || row.leadName || "录音");
    setAudioFileName(row.recordName || "");
    setAudioTranscript(cached || row.transcriptText || "");
    setAudioVisible(true);
    if (!cached && row.transcriptText) {
      try {
        const resp = await AiCoachApi.getCallLogTranscript(row.id);
        const text = (resp as any).data?.text;
        if (text) {
          setTranscriptTexts((prev) => new Map(prev).set(row.id, text));
          setAudioTranscript(text);
        }
      } catch {
        // 拉取全文失败时保留预览展示（ApiClient 已提示错误）
      }
    }
  };

  const cols: Col[] = [
    {
      name: "客户姓名",
      key: "leadName",
      showInFilter: true,
      colFilterSort: 1,
      type: ColType.STRING,
      width: 100,
    },
    {
      name: "手机号",
      key: "leadMobile",
      showInFilter: true,
      colFilterSort: 2,
      type: ColType.STRING,
      width: 120,
    },
    {
      name: "销售",
      key: "saleName",
      type: ColType.STRING,
      width: 80,
    },
    {
      name: "城市",
      key: "city",
      type: ColType.STRING,
      width: 60,
    },
    {
      name: "通话状态",
      key: "status",
      showInFilter: true,
      colFilterSort: 3,
      type: ColType.SELECT,
      width: 100,
      render: (value: string) => {
        const info:any = callStatusMap[value] || { label: value, color: "grey" };
        return (
          <Tag color={info.color} size="large" shape="circle" type="solid">
            {info.label}
          </Tag>
        );
      },
      getOptions: ():any =>
        Promise.resolve([
          { label: "拨号中", value: "CALLING" },
          { label: "已接通", value: "ENDED" },
          { label: "已接通(旧状态)", value: "CONNECTED" },
          { label: "未接通", value: "MISSED" },
        ]),
    },
    {
      name: "拨号时间",
      key: "callAt",
      type: ColType.STRING,
      width: 160,
    },
    {
      name: "挂断时间",
      key: "endAt",
      type: ColType.STRING,
      width: 160,
    },
    {
      name: "通话时长",
      key: "callTimeLong",
      type: ColType.STRING,
      width: 80,
      defaultSortOrder: false,
      render: (value: number) => {
        if (value === null || value === undefined) return "-";
        return formatSeconds(value);
      },
    },
    {
      name: "录音文件",
      key: "recordName",
      type: ColType.STRING,
      width: 180,
      render: (value: number, row:any) => {
        if (value === null || value === undefined ||  !row.recordUploaded) return "-";
        return value;
      },
    },
    {
      name: "录音",
      key: "recordUploaded",
      showInFilter: true,
      colFilterSort: 4,
      type: ColType.SELECT,
      width: 200,
      render: (value: boolean, row: any) => {
        const hasRecord = value === true && row.recordFile;
        const hasTranscript = !!(transcriptTexts.get(row.id) || row.transcriptText);
        if (hasRecord) {
          const isTranscribing = transcribing.has(row.id) || row.transcriptStatus === "processing";
          // 时长窗口预校验（后端仍会强校验）
          const durationSec = row.callTimeLong ?? 0;
          const outOfRange = !!transcriptLimits &&
            (durationSec < transcriptLimits.minDurationSec || durationSec > transcriptLimits.maxDurationSec);
          const limitsUnknown = transcriptLimits == null;
          return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Button
                theme="outline"
                type="primary"
                icon={<IconPlay />}
                onClick={() => openPlayerModal(row)}
                size="small"
              >
                播放
              </Button>
              {/* 转写服务未启用（且配置已确认）时不显示转写入口 */}
              {!hasTranscript && !limitsUnknown && !transcriptLimits.enabled && null}
              {/* 时长超出窗口：显示原因，不提供提交入口 */}
              {!hasTranscript && !limitsUnknown && transcriptLimits.enabled && outOfRange && (
                <Tooltip
                  content={`仅支持转写 ${transcriptLimits.minDurationSec}秒 ~ ${Math.round(transcriptLimits.maxDurationSec / 60)}分钟 的录音`}
                >
                  <Tag color="grey" size="small" style={{ margin: 2 }}>时长不符</Tag>
                </Tooltip>
              )}
              {/* 可转写：processing 中（本页提交或服务端已受理）按钮锁定为"转写中"，防止重复提交 */}
              {!hasTranscript && (limitsUnknown || (transcriptLimits.enabled && !outOfRange)) && (
              <Button
              theme="outline"
                type="secondary"
                icon={isTranscribing ? <IconLoading /> : undefined}
                onClick={() => handleTranscribe(row.id)}
                size="small"
                disabled={isTranscribing}
              >
                {isTranscribing ? "转写中" : "转文字"}
              </Button>
              )}
              {hasTranscript && (
                <Button
                theme="outline"
                  type="warning"
                  size="small"
                  onClick={() => openPlayerModal(row)}
                >
                  看通话内容
                </Button>
              )}
            </div>
          );
        }
        return <Tag color="grey" size="small">无录音</Tag>;
      },
      getOptions: ():any =>
        Promise.resolve([
          { label: "已上传", value: "true" },
          { label: "未上传", value: "false" },
        ]),
    },
    {
          name: "通话内容",
          key: "transcriptText",
          colSort: 8,
          hideInEdit: true,
          width: 210,
          hideInCreate: true,
          showInFilter: true,
          render: (value: any, row: any) => (
            <TextCell text={transcriptTexts.get(row.id) || (value ? value : '')} width={200} />
          ),
          type: ColType.STRING,
        },
    {
      name: "拨号时间",
      key: "callAt",
      showInFilter: true,
      hideInList: true,
      colFilterSort: 6,
      type: ColType.DATE_RANGE,
      width: 160,
    },
  ];

  // 默认筛选今天的日期（按拨号时间 callAt 过滤，与"拨号时间"列语义一致；
  // 注意存量数据 call_at 为 NULL 的行会被该筛选排除）
  const now = new Date();
  const initFilterValues = {
    callAt: [now, now],
  };
  const startDateStr = formatDate(now);
  const endDateStr = formatDate(now);
  const initParams = {
    callAt: startDateStr + "," + endDateStr,
  };

  return (
    <>
      <Crud
        canCreate={false}
        canUpdate={false}
        name="拨号录音"
        entityKey="leadCallLog"
        pageSize={50}
        rowActionWidth={0}
        cols={cols}
        initFilterValues={initFilterValues}
        initParams={initParams}
        getCrudApi={(api) => { crudApiRef.current = api; }}
      />
      {/* 录音播放 + 转写联动弹窗 */}
      <AudioPlayerModal
        visible={audioVisible}
        url={audioUrl}
        title={audioTitle}
        fileName={audioFileName}
        transcript={audioTranscript}
        onClose={closeAudio}
      />
    </>
  );
}
